import CustomLoadingButton from "@/components/Button/CustomLoadingButton";
import PermissionGuard from "@/components/Guard/PermissionGuard";
import CustomSearch from "@/components/Search/CustomSearch";
import {
  ITableButton,
  SearchField,
  SelectedAction,
} from "@/components/Table/table.interface";
import { TableEditing, useEditSummary } from "@/components/Table/editing";
import { Box, Checkbox } from "@mui/material";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import { alpha } from "@mui/material/styles";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

export default function EnhancedTableToolbar(props: EnhancedTableToolbarProps) {
  const {
    numSelected,
    title,
    buttonName,
    ButtonIcon,
    ButtonIconSecondary,
    buttonFunction,
    buttonDisabled = false,
    buttonLoading = false,
    secondaryButtonName,
    secondaryButtonFunction,
    tertiaryButtonName,
    TertiaryButtonIcon,
    tertiaryButtonFunction,
    tertiaryButtonDisabled = false,
    tertiaryButtonLoading = false,
    showButton = true,
    buttonVariant,
    handleDelete = () => null,
    selected,
    isHeader,
    isCheck,
    rowCount,
    onSelectAllClick,
    isAllSelected,
    selectedActionObject,
    primaryButtonPermission,
    searchField,
    toolbarContent,
    viewToggle,
    editing,
  } = props;
  const isEditing = Boolean(editing?.isEditing);
  const summary = useEditSummary(editing?.store);
  const blocked = summary.invalidCount > 0;

  return (
    <Toolbar
      sx={{
        pl: { sm: 2 },
        pr: { xs: 1, sm: 1 },
        // When the toolbar only hosts custom content (no title/buttons),
        // keep it slim instead of MUI's default toolbar height
        //
        // Not while editing. Save and Discard are `custom1` buttons, which the
        // theme pins at 35px, so a 30px toolbar leaves them overhanging the
        // table header — and the tables with neither a title nor a button are
        // exactly the ones in the lab, where this would be found last.
        ...(!title &&
          !buttonName &&
          !isEditing &&
          numSelected === 0 && {
            minHeight: "30px !important",
          }),
        ...(numSelected > 0 && {
          bgcolor: (theme) =>
            alpha(
              theme.palette.primary.main,
              theme.palette.action.activatedOpacity
            ),
        }),
        justifyContent: "space-between",
        // The busiest of these toolbars — title, search and a Create button —
        // already runs out of room around a 560px viewport, before the view
        // toggle asks for its 106px. Without wrapping the shortfall comes out of
        // the search field, which is the one control here that shrinks, so it
        // ends up a few characters wide. Wrapping costs nothing at any width
        // that fits and drops the controls to their own line when they don't.
        flexWrap: "wrap",
        rowGap: 1,
      }}
    >
      {numSelected > 0 ? (
        <Typography
          sx={{ flex: "1 1 100%" }}
          color="inherit"
          variant="subtitle1"
          component="div"
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {!isHeader && (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {!isHeader && isCheck && (
                  <Checkbox
                    color="primary"
                    checked={rowCount > 0 && isAllSelected}
                    onChange={onSelectAllClick}
                  />
                )}
                <Typography variant="bodyBigBold">
                  {isAllSelected ? "Unselect All" : "Select All"}
                </Typography>
              </Box>
            )}
            {numSelected} selected
          </Box>
        </Typography>
      ) : (
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {!isHeader && isCheck && (
            <Checkbox
              color="primary"
              checked={rowCount > 0 && isAllSelected}
              onChange={onSelectAllClick}
            />
          )}
          <Typography variant="bodyBigBold">{title}</Typography>
        </Box>
      )}
      {/* The view toggle sits outside the selection branch on purpose. Selecting
          a row swaps the whole right-hand side for the bulk actions, which is
          why it can't ride along in `toolbarContent` — how much of a row you can
          read is not something you should lose by ticking a checkbox. */}
      <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
        {viewToggle}
        {isEditing && editing ? (
          /* Edit mode takes the whole right-hand side, and that is the design
             rather than a consequence of running out of room: with the column
             picker and Create gone, there is no way to hide the column you are
             editing or to navigate off mid-edit. The view toggle survives — it
             is rendered inside `viewToggle`, which decides for itself. */
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            <Typography
              variant="body2"
              // The count carries the reason Save is off. There is no room here
              // for a sentence, and a disabled button with nothing saying why is
              // the worst thing this bar can do.
              color={
                blocked || summary.failedCount > 0
                  ? "error"
                  : "text.secondary"
              }
            >
              {blocked ? (
                <>
                  <b>{summary.invalidCount}</b> invalid
                  {summary.firstInvalid ? ` — ${summary.firstInvalid}` : ""}
                </>
              ) : summary.failedCount > 0 ? (
                // Said plainly, and Save stays live: the row was refused, not
                // mistyped, so pressing Save again is the expected next move.
                <>
                  <b>{summary.failedCount}</b> not saved
                  {summary.firstFailed ? ` — ${summary.firstFailed}` : ""}
                </>
              ) : (
                <>
                  <b>{summary.dirtyCount}</b>{" "}
                  {summary.dirtyCount === 1 ? "change" : "changes"}
                </>
              )}
            </Typography>
            <Button onClick={editing.cancel} disabled={editing.isSaving}>
              Discard
            </Button>
            <CustomLoadingButton
              variant={buttonVariant}
              onClick={editing.save}
              disabled={
                summary.dirtyCount === 0 || blocked || editing.isSaving
              }
              isLoading={editing.isSaving}
              title="Save changes"
            />
          </Box>
        ) : numSelected > 0 ? (
          <>
            {selectedActionObject?.map((x, i) => {
              const Icon = x.icon;
              return (
                <Tooltip key={i} title={x.type}>
                  <IconButton onClick={x.handleOnClick}>
                    <Icon />
                  </IconButton>
                </Tooltip>
              );
            })}
          </>
        ) : (
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            {toolbarContent}{" "}
            {searchField?.setSearch && (
              <CustomSearch
                type="secondary"
                placeholder="Search"
                position="start"
                styles={{ maxWidth: "250px" }}
                {...searchField}
              />
            )}
            <>
              {" "}
              {showButton && (
                <div style={{ display: "flex", gap: "10px" }}>
                  {tertiaryButtonName && (
                    <CustomLoadingButton
                      variant={buttonVariant}
                      startIcon={TertiaryButtonIcon && <TertiaryButtonIcon />}
                      onClick={tertiaryButtonFunction}
                      disabled={tertiaryButtonDisabled}
                      isLoading={tertiaryButtonLoading}
                      title={tertiaryButtonName}
                    />
                  )}
                  {buttonName &&
                    (primaryButtonPermission?.module ? (
                      <PermissionGuard {...primaryButtonPermission}>
                        <CustomLoadingButton
                          variant={buttonVariant}
                          startIcon={ButtonIcon && <ButtonIcon />}
                          onClick={buttonFunction}
                          disabled={buttonDisabled}
                          isLoading={buttonLoading}
                          title={buttonName ?? ""}
                        />
                      </PermissionGuard>
                    ) : (
                      <CustomLoadingButton
                        variant={buttonVariant}
                        startIcon={ButtonIcon && <ButtonIcon />}
                        onClick={buttonFunction}
                        disabled={buttonDisabled}
                        isLoading={buttonLoading}
                        title={buttonName ?? ""}
                      />
                    ))}
                  {secondaryButtonName && (
                    <Button
                      variant={buttonVariant}
                      startIcon={ButtonIconSecondary && <ButtonIconSecondary />}
                      onClick={secondaryButtonFunction}
                    >
                      {secondaryButtonName}
                    </Button>
                  )}
                </div>
              )}
            </>
          </Box>
        )}
      </Box>
    </Toolbar>
  );
}

interface EnhancedTableToolbarProps extends ITableButton {
  numSelected: number;
  toolbarContent?: React.ReactNode;
  /** Rendered by CustomTable when the list names a `tableId`. */
  viewToggle?: React.ReactNode;
  /**
   * Present when the table has edit mode configured. While `isEditing`, this
   * component's right-hand side becomes the save bar.
   */
  editing?: TableEditing;
  title?: string | React.ReactElement;
  handleDelete?: any;
  selected?: Array<any>;
  isHeader: boolean;
  isCheck: boolean;
  rowCount: number;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isAllSelected: boolean;
  selectedActionObject?: SelectedAction[];
  searchField?: SearchField;
}
